import { FaAngleDown } from 'react-icons/fa6';
import styled, { css } from 'styled-components';

export const TableSection = styled.section<{ minHeight?: string }>`
  padding: 0 5px 20px 5px;
  background-color: ${({ theme }) => theme.colors.white};
  ${({ minHeight }) =>
    minHeight &&
    css`
      min-height: ${minHeight};
    `}
  padding: 0px 5px 20px 5px;
  border: 1px solid #e5e5e5;
  border-radius: 20px;
  max-height: 100%;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 20px;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 640px;
`;

export const HeaderRow = styled.tr`
  background: ${({ theme }) => theme.colors.white};
`;

export const HeaderCell = styled.th<{ $sortable: boolean }>`
  padding: 14px 16px;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.tabela.fonteTh};
  border-bottom: 2px solid ${({ theme }) => theme.colors.tabela.linha};
  ${({ $sortable }) =>
    $sortable &&
    css`
      cursor: pointer;
      user-select: none;
    `}
`;

export const HeaderContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const FilterRow = styled.tr`
  background: ${({ theme }) => theme.colors.filtros.fundo};
`;

export const FilterActionCell = styled.th`
  padding: 10px 12px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.tabela.linha};
  text-align: center;
  background: ${({ theme }) => theme.colors.filtros.fundo};
`;

export const FilterCell = styled.th`
  padding: 10px 16px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.tabela.linha};
  background: ${({ theme }) => theme.colors.filtros.fundo};
  font-weight: 400;
`;

export const FilterCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const FilterControlWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

export const FilterCheckboxWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.tabela.fonteTd};
  font-size: 0.8125rem;
`;

export const FilterCheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const filterControlStyles = css`
  width: 100%;
  height: 36px;
  border: 1px solid ${({ theme }) => theme.colors.tabela.linha};
  border-radius: 10px;
  padding: 0 12px;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.tabela.fonteTd};
  font-size: 0.8125rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.tabela.barra};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.tabela.barra}33;
  }
`;

export const FilterInput = styled.input`
  ${filterControlStyles}
`;

export const FilterSelect = styled.select`
  ${filterControlStyles}
`;

export const ClearFiltersButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) => theme.colors.tabela.linha};
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.tabela.fonteTh};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme }) => theme.colors.tabela.fundo};
    color: ${({ theme }) => theme.colors.tabela.fonteActive};
  }
`;

export const InlineClearFiltersButton = styled(ClearFiltersButton)`
  width: 32px;
  height: 32px;
`;

export const BodyRow = styled.tr<{ $clickable: boolean }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.tabela.linha};
  background: ${({ theme }) => theme.colors.white};

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover {
        background: ${theme.colors.tabela.fundo};
      }
    `}
`;

export const BodyCell = styled.td`
  padding: 12px 16px;
  color: ${({ theme }) => theme.colors.tabela.fonteTd};
  font-size: 0.875rem;
  word-break: break-word;
`;

export const IconCell = styled(BodyCell)`
  width: auto;
  padding: 12px 15px;
`;

export const RowActionButton = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.colors.tabela.fonteTd};
  cursor: pointer;
  user-select: none;
  font-size: 24px;
  line-height: 1;
`;

export const EmptyMessage = styled.p`
  padding: 40px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.tabela.fonteTd};
  font-size: 1rem;
`;

export const Footer = styled.footer`
  padding: 0 20px;
  margin-top: 10px;
`;

export const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
`;

export const ShowingText = styled.p`
  color: ${({ theme }) => theme.colors.tabela.fonteTd};
  margin-bottom: 10px;
  font: normal normal medium 0.875rem/1.1875rem Rawline;
`;

export const TotalText = styled.span`
  color: ${({ theme }) => theme.colors.tabela.fonteTh};
`;

export const PaginationNav = styled.nav`
  width: 100%;
`;

export const PaginationContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
`;

export const PaginationList = styled.ul`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: center;
`;

export const PaginationItem = styled.li`
  &:first-child button {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  &:last-child button {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

export const PaginationButton = styled.button<{ $active?: boolean }>`
  display: block;
  padding: 5px 10px;
  height: 37px;
  width: 37px;
  text-align: center;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.tabela.paginaBd};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.tabela.fonteTh};
  text-decoration: none;

  &:focus {
    outline: none;
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background-color: ${theme.colors.tabela.fundo};
      color: ${theme.colors.tabela.fonteActive};
      letter-spacing: 0px;
      opacity: 1;
    `}

  &:disabled {
    pointer-events: none;
    background-color: ${({ theme }) => theme.colors.tabela.fundo};
    color: ${({ theme }) => theme.colors.tabela.fonteTh};
  }
`;

export const SelectContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const ItemsPerPageSelect = styled.select`
  height: 37px;
  padding: 8px 30px 8px 15px;
  border: 1px solid ${({ theme }) => theme.colors.tabela.linha};
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.tabela.fonteTd};
  font: normal normal medium 0.875rem/1.1875rem Rawline;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="0" height="0"><path d="" fill="none"/></svg>');
  background-repeat: no-repeat;
  background-position: right 10px center;

  &:focus {
    outline: none;
    border: 1px solid ${({ theme }) => theme.colors.tabela.linha};
  }
`;

export const SelectAngleDownIcon = styled(FaAngleDown)`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.tabela.fonteTh};
  pointer-events: none;
`;

export const PaginationForm = styled.form`
  display: flex;
  align-items: center;
`;

export const PaginationLabel = styled.label`
  display: flex;
  align-items: center;
  margin-right: 10px;
  position: relative;
`;

export const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const GoToPageInput = styled.input`
  width: 150px;
  padding: 5px 30px 5px 20px;
  border: 1px solid ${({ theme }) => theme.colors.tabela.linha};
  color: ${({ theme }) => theme.colors.tabela.fonteTd};
  border-radius: 10px;
  text-align: left;
  font: normal normal medium 0.875rem/1.1875rem Rawline;

  &:focus {
    outline: none;
  }
`;

export const GoToPageButton = styled.button`
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.tabela.fonteTh};
  font-size: 1rem;
`;
